import {
  computed,
  inject,
  Injectable,
  Injector,
  runInInjectionContext,
  signal,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { map, Observable } from 'rxjs';
import { PartialDeep } from 'type-fest';

import {
  CanvasElementFeatures,
  CanvasElementFrom,
  CanvasElementId,
  CanvasElementInput,
  CanvasElementType,
  IoConfig,
  PartialPropsOf,
} from '@blaze-runtime/engine-domain';
import { AuthService } from '@blaze-shared/auth-data-access';
import { CanvasElementInputBinding } from '@blaze-shared/elements-domain';
import { BlazeSchema, type RuntimeEventType } from '@blaze-shared/schema-domain';
import { deepClone } from '@blaze-shared/shared-util';

import { BaseAiElementDefinition } from '../ai/models';
import { DefinitionMapper } from '../ai/page/definition-mapper';
import {
  CanvasElementDefinition,
  CanvasElementDefinitionInternal,
  stripClassTypes,
} from '../canvas-elements/element-definition';
import { getDefaultOutputEventType } from '../canvas-elements/element-model/io';
import { type ParentChildrenOverrides } from '../canvas-elements/parent-overrides';
import { PropsBuilder } from '../elements/__base/props-builder';
import { PropsExtractor } from '../elements/__base/props-extractor';

import { validateElementDefinition } from './element-definitions-validations';

let service: ElementDefinitionsService;
const definitionQueue: CanvasElementDefinition[] = [];

// TODO: unit test
export function registerElementDefinition<T extends CanvasElementType>(
  elementDefinition: CanvasElementDefinition<T>,
): void {
  // If service not yet initialized, queue the definitions
  if (!service) {
    definitionQueue.push(elementDefinition);
  } else {
    service.register(elementDefinition);
  }
}

/**
 * Definitions registered while no service instance exists yet. Offline tooling
 * (tools/ai-theme) imports the element barrel without bootstrapping Angular, so
 * the queue is never drained and holds every registration. Inside the running
 * app the service drains it on construction and this returns an empty list.
 */
export function getQueuedElementDefinitions(): readonly CanvasElementDefinition[] {
  return definitionQueue;
}

export type GetPropsExtractorFn = <T extends CanvasElementType>(
  type: T,
  props?: PartialPropsOf<T>,
) => PropsExtractor<T> | undefined;

// TODO: unit tests
@Injectable({ providedIn: 'root' })
export class ElementDefinitionsService {
  private readonly authService = inject(AuthService);
  private readonly injector = inject(Injector);

  private readonly elementDefinitions = signal<CanvasElementDefinition[]>([]);
  private readonly elementDefinitionsMapByType = computed(() =>
    this.elementDefinitions().reduce(
      (result, def) => {
        result[def.type] = def;
        return result;
      },
      {} as Record<CanvasElementType, CanvasElementDefinition>,
    ),
  );

  listedElementDefinitions$: Observable<CanvasElementDefinition[]>;
  elementDefinitions$ = toObservable(this.elementDefinitions);

  constructor() {
    this.listedElementDefinitions$ = this.elementDefinitions$.pipe(
      map((definitions) => definitions.filter(this.resolveElementAvailability.bind(this))),
    );

    // 'this' aliasing made here on purpose so there's no difference for the elements whether they register before or after this service is initialized
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    service = this;

    // Register queued definitions
    while (definitionQueue.length) {
      const def = definitionQueue.shift();
      if (def) {
        this.register(def);
      }
    }
  }

  getDefaultOutputEventType(type: CanvasElementType | undefined): RuntimeEventType | undefined {
    return getDefaultOutputEventType(this.getIoConfig(type));
  }

  resolveTypeByAiName(aiName: string | undefined): CanvasElementType | undefined {
    return aiName ? this.getDefinitionByAiName(aiName)?.type : undefined;
  }

  /**
   * Returns a new instance of the definition.
   *
   * It is safe to use for creation of new elements.
   *
   * The instance is a **deep copy** and is checked for existence. If provided type is missing an error is thrown.
   * Also, the definition is **stripped** from the `componentType` class, so it's serializable and safe to be put in actions.
   */
  getDefinitionSafe<T extends CanvasElementType>(
    type: T | undefined,
  ): CanvasElementDefinitionInternal<T> {
    const definition = this.getDefinition(type);
    if (!definition) {
      throw new Error(`Could not get the definition for type '${type}'`);
    }

    return deepClone(stripClassTypes(definition));
  }

  /**
   * Use with caution, the returned value of this method should not be mutated.
   * Most often what you need is {@link getDefinitionSafe}.
   */
  getDefinition<T extends CanvasElementType>(
    type: T | undefined,
  ): CanvasElementDefinition<T> | undefined {
    if (!type) {
      return undefined;
    }

    return this.elementDefinitionsMapByType()[type] as CanvasElementDefinition<T> | undefined;
  }

  getDefinitionByAiName<T extends CanvasElementType>(
    name: string,
  ): CanvasElementDefinition<T> | undefined {
    return this.elementDefinitions().find((element) => element.ai?.name === name) as
      | CanvasElementDefinition<T>
      | undefined;
  }

  getDefinitionByAiNameSafe<T extends CanvasElementType>(
    name: string,
  ): CanvasElementDefinitionInternal<T> {
    const definition = this.elementDefinitions().find((element) => element.ai?.name === name);

    if (!definition) {
      throw new Error(`Could not find the definition for ${name}`);
    }

    return deepClone(stripClassTypes(definition)) as CanvasElementDefinitionInternal<T>;
  }

  /**
   * Maps a canvas element to its AI-format definition using the type's registered
   * `definitionMapper`. Returns `null` when the type has no AI mapping configured.
   */
  extractAiDefinition(
    element: CanvasElementFrom<any>,
    schemas: Readonly<Record<string, BlazeSchema>>,
  ): PartialDeep<BaseAiElementDefinition<any>> | null {
    const baseDefinition = this.getDefinition(element.type);
    const mapper = this.resolveDefinitionMapper(element.type);
    if (!baseDefinition?.ai || !mapper) {
      return null;
    }

    const definition = mapper.extract(element, schemas, baseDefinition);
    definition.$type = baseDefinition.ai.name;
    return definition;
  }

  resolveDefinitionMapper<T extends CanvasElementType>(type: T): DefinitionMapper<T> | null {
    const MapperFactory = this.getDefinition(type)?.ai?.definitionMapper;
    if (!MapperFactory) {
      return null;
    }

    const MapperClass = MapperFactory();
    return runInInjectionContext(this.injector, () => new MapperClass());
  }

  /** Returns element types with the feature set to a specific value */
  getTypesWithFeature<T = unknown>(
    predicate: (value: CanvasElementFeatures) => T,
    value: T,
  ): CanvasElementType[] {
    return this.elementDefinitions()
      .filter((def) => (def.features ? predicate(def.features) === value : false))
      .map((def) => def.type);
  }

  getFeature<FeatureKey extends keyof CanvasElementFeatures>(
    type: CanvasElementType,
    predicate: (value: CanvasElementFeatures) => any,
  ): CanvasElementFeatures[FeatureKey] | undefined;

  getFeature<FeatureKey extends keyof CanvasElementFeatures>(
    type: CanvasElementType,
    feature: FeatureKey,
  ): CanvasElementFeatures[FeatureKey] | undefined;

  getFeature<FeatureKey extends keyof CanvasElementFeatures>(
    type: CanvasElementType,
    arg: FeatureKey | ((value: CanvasElementFeatures) => any),
  ): CanvasElementFeatures[FeatureKey] | undefined {
    const features = this.getDefinition(type)?.features;
    if (!features) {
      return undefined;
    }
    if (typeof arg === 'function') {
      return arg(features);
    }

    return features[arg];
  }

  getIoConfig(type: CanvasElementType | undefined): IoConfig | undefined {
    return this.getDefinition(type)?.ioConfig;
  }

  getInputConfig(
    type: CanvasElementType | undefined,
    name: CanvasElementInputBinding,
  ): CanvasElementInput | undefined {
    return this.getIoConfig(type)?.inputs?.find((input) => input.name === name);
  }

  /** Whether the element type styles its children at all ("Style All Fields" family). */
  hasChildrenOverridesResolver(type: CanvasElementType | undefined): boolean {
    return !!this.getDefinition(type)?.childrenOverridesResolver;
  }

  /**
   * The children overrides bundle a parent applies onto its children, produced by its
   * definition's `childrenOverridesResolver`. Typed accessor - it keeps the element type
   * bound to its own props, the same way {@link getPropsBuilder} does, so the resolver is
   * never handed another element type's props.
   */
  getChildrenOverrides<T extends CanvasElementType>(
    type: T,
    parentProps: PartialPropsOf<T>,
    childId: CanvasElementId,
  ): ParentChildrenOverrides | undefined {
    return this.getDefinition(type)?.childrenOverridesResolver?.(parentProps, childId);
  }

  getPropsBuilder<T extends CanvasElementType>(
    type: T,
    props?: PartialPropsOf<T>,
  ): PropsBuilder<T> | undefined {
    const definition = this.getDefinition(type);
    if (!definition) {
      return;
    }

    const propsBuilderClass = definition.propsBuilder || PropsBuilder<T>;
    return new propsBuilderClass(props);
  }

  getPropsExtractor<T extends CanvasElementType>(
    type: T,
    props?: PartialPropsOf<T>,
  ): PropsExtractor<T> | undefined {
    const definition = this.getDefinition(type);
    if (!definition) {
      return;
    }

    const propsExtractorClass = definition.propsExtractor || PropsExtractor<T>;
    return new propsExtractorClass(props);
  }

  register(elementDefinition: CanvasElementDefinition): void {
    if (this.getDefinition(elementDefinition.type)) {
      console.error(`Duplicate element type: ${elementDefinition.type}`, elementDefinition);
    } else {
      validateElementDefinition(elementDefinition);

      this.elementDefinitions.set([...this.elementDefinitions(), elementDefinition]);
    }
  }

  getDefaultDataSourceInput(type: CanvasElementType | undefined): CanvasElementInput | undefined {
    return this.getIoConfig(type)?.inputs?.find((input) => input.isDefaultDataSourceInput);
  }

  getQueryableInput(type: CanvasElementType | undefined): CanvasElementInput | undefined {
    return this.getIoConfig(type)?.inputs?.find((input) => input.isQueryable);
  }

  private resolveElementAvailability(def: CanvasElementDefinition): boolean {
    return (
      def.isListed &&
      (!def.availableFor ||
        (def.availableFor === 'root' && this.authService.isCurrentUserAccountRoot()) ||
        (def.availableFor === 'admin' && this.authService.isCurrentUserAccountAdmin()) ||
        (def.availableFor === 'dev' && this.authService.isCurrentUserAccountDev()))
    );
  }
}
